import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export interface CaseTab {
  caseId: string;
  name: string;
  status?: string;
  progress?: number; // 0–100, for analysis progress indicator
  lastTab?: string;  // last-visited subtab id (e.g. 'overview', 'records') — restored on case switch
}

interface OpenCasesContextType {
  openCases: CaseTab[];
  activeCaseId: string | null;
  openCase: (caseId: string, name: string, status?: string) => void;
  closeCase: (caseId: string) => void;
  setActiveCase: (caseId: string) => void;
  updateCaseTab: (caseId: string, updates: Partial<Omit<CaseTab, 'caseId'>>) => void;
  reorderCases: (orderedIds: string[]) => void;
}

const STORAGE_KEY = 'clinlexis_open_case_tabs';
const MAX_TABS = 5;

const OpenCasesContext = createContext<OpenCasesContextType | null>(null);

function loadPersistedState(): { tabs: CaseTab[]; activeCaseId: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tabs: [], activeCaseId: null };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.tabs) && parsed.tabs.every((t: any) => t.caseId && t.name)) {
      return { tabs: parsed.tabs, activeCaseId: parsed.activeCaseId || null };
    }
  } catch { /* corrupt data, ignore */ }
  return { tabs: [], activeCaseId: null };
}

function persistState(tabs: CaseTab[], activeCaseId: string | null) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeCaseId }));
  } catch { /* storage full, ignore */ }
}

export const OpenCasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openCases, setOpenCases] = useState<CaseTab[]>(() => loadPersistedState().tabs);
  const [activeCaseId, setActiveCaseIdState] = useState<string | null>(() => loadPersistedState().activeCaseId);

  // Persist on every change
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    persistState(openCases, activeCaseId);
  }, [openCases, activeCaseId]);

  // Reset in-memory state on logout to prevent leaking across accounts
  useEffect(() => {
    const handleLogout = () => {
      setOpenCases([]);
      setActiveCaseIdState(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const openCase = useCallback((caseId: string, name: string, status?: string) => {
    setOpenCases(prev => {
      const existing = prev.find(t => t.caseId === caseId);
      if (existing) return prev; // already open
      let next = [...prev, { caseId, name, status }];
      // Evict oldest non-active tab if over limit
      if (next.length > MAX_TABS) {
        const evictIndex = next.findIndex(t => t.caseId !== caseId);
        if (evictIndex !== -1) next.splice(evictIndex, 1);
      }
      return next;
    });
    setActiveCaseIdState(caseId);
  }, []);

  const closeCase = useCallback((caseId: string) => {
    setOpenCases(prev => {
      const index = prev.findIndex(t => t.caseId === caseId);
      if (index === -1) return prev;
      const next = prev.filter(t => t.caseId !== caseId);
      return next;
    });
    setActiveCaseIdState(prev => {
      if (prev !== caseId) return prev;
      // Switch to nearest tab
      return null;
    });
  }, []);

  const setActiveCase = useCallback((caseId: string) => {
    setActiveCaseIdState(caseId);
  }, []);

  const updateCaseTab = useCallback((caseId: string, updates: Partial<Omit<CaseTab, 'caseId'>>) => {
    setOpenCases(prev => {
      const index = prev.findIndex(t => t.caseId === caseId);
      if (index === -1) return prev;
      const updated = { ...prev[index], ...updates };
      // Avoid unnecessary re-renders if nothing changed
      if (prev[index].name === updated.name && prev[index].status === updated.status && prev[index].progress === updated.progress && prev[index].lastTab === updated.lastTab) return prev;
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const reorderCases = useCallback((orderedIds: string[]) => {
    setOpenCases(prev => {
      const map = new Map(prev.map(t => [t.caseId, t]));
      return orderedIds.map(id => map.get(id)).filter(Boolean) as CaseTab[];
    });
  }, []);

  return (
    <OpenCasesContext.Provider value={{ openCases, activeCaseId, openCase, closeCase, setActiveCase, updateCaseTab, reorderCases }}>
      {children}
    </OpenCasesContext.Provider>
  );
};

export const useOpenCases = (): OpenCasesContextType => {
  const context = useContext(OpenCasesContext);
  if (!context) throw new Error('useOpenCases must be used within an OpenCasesProvider');
  return context;
};
